import { CommonModule } from '@angular/common';
import { ErrorHandler, NgModule, NgZone } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Http, HttpModule } from '@angular/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppVersion } from '@ionic-native/app-version';
import { Camera } from '@ionic-native/camera';
import { Device } from '@ionic-native/device';
import { EmailComposer } from '@ionic-native/email-composer';
import { File as NativeFile } from '@ionic-native/file';
import { FileOpener } from '@ionic-native/file-opener';
import { FileTransfer } from '@ionic-native/file-transfer';
import { MediaCapture } from '@ionic-native/media-capture';
import { NativeStorage } from '@ionic-native/native-storage';
import { Network } from '@ionic-native/network';
import { ScreenOrientation } from '@ionic-native/screen-orientation';
import { Toast } from '@ionic-native/toast';
import { Actions, EffectsModule } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateService } from '@ngx-translate/core';
import { AuthHttp } from 'angular2-jwt';
import {
    AlertController,
    IonicErrorHandler,
    LoadingController,
    Platform,
    PopoverController,
    ToastController
} from 'ionic-angular';
import { VASRestService } from '../core/services/api/vas-rest';
import { GuardService } from '../core/services/guard/guard.service';
import { MessageBusService } from '../core/services/message-bus/message-bus.service';
import { NormalizationService } from '../core/services/normalization/normalization.service';
import { SystemSideEffects } from '../core/system/system.side-effects';
import { AccountEffects } from '../vas/business-objects/account/account.effects';
import { ConfigSideEffects } from '../vas/business-objects/config/config.side-effects';
import { FileDownloadEffects } from '../vas/business-objects/file/file-download.effects';
import { FileShareEffects } from '../vas/business-objects/file/file-share.effects';
import { FileEffects } from '../vas/business-objects/file/file.effects';
import { JobsSideEffects } from '../vas/business-objects/job/jobs.side-effects';
import { MembershipSideEffects } from '../vas/business-objects/membership/membership.side-effects';
import { SyncUpItemSideEffects } from '../vas/business-objects/sync-up-item/sync-up-item.side-effects';
import { TaskSideEffectsService } from '../vas/business-objects/task/task-side-effects.service';
import { AppJobsController } from '../vas/controllers/app-jobs.controller';
import { TextControlEffects } from '../vas/controls/text/form/text-control.effects';
import { IonContentElementQuery } from '../vas/directives/ion-content-selector.directive';
import { CalendarEffects } from '../vas/pages/job-calendar/calendar.effects';
import { ExportService } from '../vas/services/export.service';
import { FormHelperService } from '../vas/services/form-helper-service/form-helper.service';
import { ShareService } from '../vas/services/share/share.service';
import { UniversalFilesystemService } from '../vas/services/universal-filesystem/universal-filesystem.service';
import { VasAppFacade } from '../vas/services/vas-app-facade.service';
import { JobStatusTransitionService } from '../vas/services/work-flow/job-status-transition.service';
import { reducer } from '../vas/store/bootstrap';
import { CollectionEffects } from '../vas/store/effects/collection';
import { ValidatorsFactory } from '../vas/validators/validators-factory';
import { AppConfig } from './app.config';
import {
    getAuthHttp,
    getStorageServiceForChangeLogQ,
    getStorageServiceForState,
    remoteThumbnailServiceFactory
} from './app.module.helpers';
import {
    EmailContactPickerDataProviderService
} from './atoms/email-contact-picker/email-contact-picker-data-provider.service';
import { JobPickerControlDataProviderService } from './atoms/job-picker/job-picker-control-data-provider.service';
import { JobStatusPickerPopoverEffects } from './atoms/job-status-picker-popover/job-status-picker-popover.effects';
import {
    SearchAsYouTypeDefaultDataProviderService
} from './atoms/search-as-you-type/search-as-you-type-default-data-provider.service';
import { SEARCH_AS_YOU_TYPE_DATA_PROVIDER_SERVICE } from './atoms/search-as-you-type/search-as-you-type.tokens';
import { DirectivesModule } from './directives/directives.module';
import { CHANGE_LOG_STORAGE_TOKEN, STATE_STORAGE_TOKEN } from './injection-tokens';
import { CORE_FILESYSTEM_TOKEN } from './injection-tokens/universal-file-system.token';
import {
    TextControlMobileModalEffects
} from './modals-popovers/text-control-mobile-modal/text-control-mobile-modal.effects';
import { FileManagerEffects } from './molecules/file-manager/file-manager.effects';
import { FormManagerPageEffects } from './pages/form-manager-page/form-manager-page.effects';
import { CordovaThumbnailService } from './services/cordova-thumbnail.service';
import { errorHandlerServiceFactory, SENTRY_DSN_TOKEN } from './services/error-handler.service';
import { FileManagerService } from './services/file-manager.service';
import { RemoteThumbnailService } from './services/remote-thumbnail.service';
import { ReportService } from './services/report.service';

// const cloudSettings: /* CloudSettings */ any = {
//     'core': {
//         'app_id': '555218e3'
//     },
//     'push': {
//         'sender_id': '555951813762',
//         'pluginConfig': {
//             'ios': {
//                 'badge': true,
//                 'sound': true
//             },
//             'android': {
//                 'iconColor': '#343434'
//             }
//         }
//     }
// };

@NgModule({
    imports: [
        StoreModule.provideStore(reducer),
        StoreDevtoolsModule.instrumentOnlyWithExtension(),
        EffectsModule.run(CollectionEffects),
        EffectsModule.runAfterBootstrap(SystemSideEffects),
        EffectsModule.runAfterBootstrap(ConfigSideEffects),
        EffectsModule.runAfterBootstrap(TaskSideEffectsService),
        EffectsModule.runAfterBootstrap(JobsSideEffects),
        EffectsModule.runAfterBootstrap(SyncUpItemSideEffects),
        EffectsModule.runAfterBootstrap(MembershipSideEffects),
        EffectsModule.runAfterBootstrap(JobStatusPickerPopoverEffects),
        EffectsModule.runAfterBootstrap(FileEffects),
        EffectsModule.runAfterBootstrap(FileDownloadEffects),
        EffectsModule.runAfterBootstrap(FileShareEffects),
        EffectsModule.runAfterBootstrap(FileManagerEffects),
        EffectsModule.runAfterBootstrap(FormManagerPageEffects),
        EffectsModule.runAfterBootstrap(AccountEffects),
        EffectsModule.runAfterBootstrap(TextControlEffects),
        EffectsModule.runAfterBootstrap(TextControlMobileModalEffects),
        EffectsModule.runAfterBootstrap(CalendarEffects),
        HttpModule,
        ReactiveFormsModule,
        CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        DirectivesModule
    ],
    providers: [
        AppConfig,
        Device,
        NativeStorage,
        Camera,
        MediaCapture,
        NormalizationService,
        Toast,
        TranslateService,
        AlertController,
        LoadingController,
        MessageBusService,
        EmailComposer,
        NativeFile,
        ShareService,
        ValidatorsFactory,
        AppVersion,
        ScreenOrientation,
        Network,
        FileTransfer,
        FileOpener,
        {
            provide: CordovaThumbnailService,
            useClass: CordovaThumbnailService,
            deps: [NativeFile]
        },
        {
            provide: RemoteThumbnailService,
            deps: [Http, Store],
            useFactory: remoteThumbnailServiceFactory
        },
        {
            provide: SEARCH_AS_YOU_TYPE_DATA_PROVIDER_SERVICE,
            useClass: SearchAsYouTypeDefaultDataProviderService,
            multi: true
        },
        {
            provide: SEARCH_AS_YOU_TYPE_DATA_PROVIDER_SERVICE,
            useClass: JobPickerControlDataProviderService,
            deps: [VasAppFacade],
            multi: true
        },
        {
            provide: SEARCH_AS_YOU_TYPE_DATA_PROVIDER_SERVICE,
            useClass: EmailContactPickerDataProviderService,
            deps: [VasAppFacade],
            multi: true
        },
        {
            provide: STATE_STORAGE_TOKEN,
            useFactory: getStorageServiceForState,
            deps: [
                NativeStorage,
                Platform
            ]
        },
        {
            provide: CHANGE_LOG_STORAGE_TOKEN,
            useFactory: getStorageServiceForChangeLogQ,
            deps: [
                NativeStorage,
                Platform
            ]
        },
        {
            provide: FileManagerService,
            useClass: FileManagerService,
            deps: [
                VasAppFacade
            ]
        },
        {
            provide: FormHelperService,
            useClass: FormHelperService,
            deps: [
                VasAppFacade,
                ValidatorsFactory,
                FileManagerService
            ]
        },
        {
            provide: ErrorHandler,
            useClass: IonicErrorHandler
        },
        {
            provide: AuthHttp,
            useFactory: getAuthHttp,
            deps: [Store, Http]
        },
        {
            provide: VASRestService,
            useClass: VASRestService,
            deps: [
                AppConfig,
                Http,
                AuthHttp,
                NormalizationService,
                Store,
                TranslateService
            ]
        },
        {
            provide: CORE_FILESYSTEM_TOKEN,
            useClass: UniversalFilesystemService,
            deps: [
                NativeFile
            ]
        },
        {
            provide: SENTRY_DSN_TOKEN,
            useValue: {
                uri: 'https://o463862.ingest.sentry.io',
                publicKey: 'a9d2b3484c50438e97fa652bc225e64f',
                projectId: '5471141',
                secretKey: '26f73d79a99b47a7acf3c36ff0352f3140aa12ba0b8b491f8a41ac83da7a6204'
            }
        },
        {
            provide: ErrorHandler,
            useFactory: errorHandlerServiceFactory,
            deps: [SENTRY_DSN_TOKEN, Http]
        },
        {
            provide: VasAppFacade,
            useClass: VasAppFacade,
            deps: [
                VASRestService,
                Store,
                STATE_STORAGE_TOKEN,
                CHANGE_LOG_STORAGE_TOKEN,
                AppConfig,
                TranslateService,
                ToastController,
                LoadingController,
                AlertController,
                PopoverController,
                Platform,
                NormalizationService,
                CORE_FILESYSTEM_TOKEN,
                AppVersion,
                Http,
                NgZone,
                ErrorHandler,
                FileTransfer,
                FileOpener,
                NativeFile,
                ShareService,
                Actions,
                CordovaThumbnailService,
                RemoteThumbnailService
            ]
        },
        {
            provide: GuardService,
            useClass: GuardService,
            deps: [
                VasAppFacade
            ]
        },
        AppJobsController,
        {
            provide: JobStatusTransitionService,
            useClass: JobStatusTransitionService,
            deps: [
                VasAppFacade
            ]
        },
        {
            provide: ExportService,
            useClass: ExportService,
            deps: [
                VASRestService
            ]
        },
        ReportService
    ],
    declarations: [
        IonContentElementQuery
    ]
})
export class CoreModule {

}



// WEBPACK FOOTER //
// ./src/app/core.module.ts